import esriId from '@arcgis/core/identity/IdentityManager';
import OAuthInfo from '@arcgis/core/identity/OAuthInfo';

import type { MapConfiguration } from '@/common/overmind/namespaces/arcgis/config/config.types';

// @ts-ignore
let _config: () => MapConfiguration | undefined = null;
let _tryStoringCalculationContext: () => void = () => {};

export const initialize = ({
  config,
  tryStoringCalculationContext
}: {
  config: () => MapConfiguration | undefined;
  tryStoringCalculationContext: () => void;
}) => {
  if (!config) throw new Error('Map configuration is not provided.');
  _config = config;
  _tryStoringCalculationContext = tryStoringCalculationContext;
};
// @ts-ignore
window.esriId = esriId;
export const registerTenants = async (config: MapConfiguration) => {
  const oauthInfos = new OAuthInfo({
    appId: config.esriId.oAuthInfo.appId,
    portalUrl: config.esriId.oAuthInfo.portalUrl,
    popup: false
  });

  try {
    // if this doesn't throw, the user is signed in to the portal already
    return await esriId.checkSignInStatus(getSharingUrl(config));
  } catch {
    removeOldRegistrations(); //  its a hack - try find better way. esriId.serverInfos[0].tokenServiceUrl was missing in flow wirh arcgis token in calculation query parameter
    esriId.registerOAuthInfos([oauthInfos]);
  }
};

export const getAccessToken = (options?: {
  withMaxAttempts?: number;
}): Promise<{ arcgisToken: string | undefined }> => {
  const maxAttempts = options?.withMaxAttempts || 100;
  let attempt = 0;
  return new Promise(resolve => {
    const timer = setInterval(() => {
      attempt++;
      if (attempt > maxAttempts) {
        clearInterval(timer);
        resolve({ arcgisToken: undefined });
      }
      const config = _config && _config();
      if (!config) return;
      esriId.checkSignInStatus(getSharingUrl(config)).then(_ => {
        const arcgisToken = getToken(config);
        if (arcgisToken) {
          clearInterval(timer);
          resolve({ arcgisToken });
        }
      });
    }, 300);
  });
};

export const signIn = async (config?: MapConfiguration) => {
  if (!config) return;
  const sharingUrl = getSharingUrl(config);
  try {
    // if this doesn't throw, the user is signed in to the portal already
    return await esriId.checkSignInStatus(sharingUrl);
  } catch {
    // not signed in, perform sign in
    return await esriId.getCredential(sharingUrl);
  }
};

export const signOut = () => esriId.destroyCredentials();

export const getToken = (config?: MapConfiguration) =>
  config ? esriId.findCredential(getSharingUrl(config))?.token : undefined;

export const getCredential = async (config?: MapConfiguration) => {
  if (!config) return;
  try {
    return await esriId.checkSignInStatus(getSharingUrl(config));
  } catch {
    // We're not authenticated, return nothing.
  }
};

const getSharingUrl = (config: MapConfiguration) =>
  config.esriId.oAuthInfo.sharingUrl ?? `${config.esriId.oAuthInfo.portalUrl}/sharing`; // .portalUrl + '/sharing';

function removeOldRegistrations() {
  const infos = (esriId as any).serverInfos;
  const lenght = infos.length;
  for (let i = 0; i < lenght; i++) {
    infos.pop();
  }
}

export const registerExternalToken = (
  config: MapConfiguration,
  token: string
) => {
  const server = getSharingUrl(config);
  esriId.registerToken({ server, token });
};

export const isAuthenticated = async (config?: MapConfiguration) => {
  if (!config) return;

  const sharingUrl = getSharingUrl(config);
  try {
    // if this doesn't throw, the user is signed in to the portal already
    await esriId.checkSignInStatus(sharingUrl);
    return true;
  } catch {
    return false;
  }
};

export const routeToRedirectUri = () => {
  // due to expired arcgis token, app is going to redirect to arcigs portal
  // to enforce user signining in again but the current route needs to match the redirect_uri
  // which is configured in the ArcGIS Portal as "/signin-arcgis"
  window.onbeforeunload = () => {
      setImmediate(() => {
        _tryStoringCalculationContext()
        window.location.href = `${window.location.origin}/signin-arcgis`;
      });
      window.onbeforeunload = null;
    }
}
